import { useState, useEffect, useCallback } from 'react';
import type { SandboxProfile } from '../types/sandbox';
import { supabase } from '../lib/supabase';
import { getMockProfileByUsername } from '../lib/mock-data';

export function useProfile(username?: string) {
  const [profile, setProfile] = useState<SandboxProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!username) {
      setProfile(null);
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        if (supabase) {
          const { data, error: dbError } = await supabase
            .from('sandbox_profiles')
            .select('*')
            .eq('username', username)
            .single();
          if (dbError) throw new Error(dbError.message);
          setProfile(data as SandboxProfile);
        } else {
          const mock = getMockProfileByUsername(username);
          if (mock) {
            setProfile(mock);
          } else {
            throw new Error('Profile not found');
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [username]);

  const updateProfile = useCallback(async (updates: Partial<SandboxProfile>) => {
    if (!profile) return;

    if (supabase) {
      const { data, error: dbError } = await supabase
        .from('sandbox_profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('operator_did', profile.operator_did)
        .select()
        .single();
      if (dbError) throw new Error(dbError.message);
      setProfile(data as SandboxProfile);
    } else {
      setProfile({ ...profile, ...updates });
    }
  }, [profile]);

  return { profile, loading, error, updateProfile };
}
